import {
  FLOATING_BUTTONS_HIDDEN_CLASS,
  FLOATING_BUTTONS_TRANSITION,
  useAssistantFabMounted
} from "@/components/support/useFloatingButtonsLift";
import { cn } from "@repo/design-system/utils";
import { Sparkles } from "lucide-react";

import { useOrderAssistant } from "./OrderAssistantProvider";

/** The assistant's name: the entry button and the drawer heading say the same. */
export const ORDER_ASSISTANT_TITLE = "Order assistant";

/**
 * The order assistant's entry point: the third button in the bottom-right
 * stack, above voice actions and WhatsApp support. Like them it is a 44px icon
 * button; from `sm` up it expands leftwards to a labelled pill while hovered or
 * keyboard-focused (`max-width` transition, instant under reduced motion), so
 * the label never covers page content at rest. Hover only counts on devices
 * that really hover (`@media (hover: hover)`): on a touch tablet a tap would
 * otherwise leave the pill stuck open. The accessible name is exactly the
 * visible label in both states (WCAG 2.5.3); `aria-haspopup` and
 * `aria-expanded` say that it opens a dialog.
 *
 * Geometry is mirrored in `useFloatingButtonsLift`: it stacks at `bottom-[7.75rem]`
 * (124px = 12 + 44 + 12 + 44 + 12) and shares the `right-2 / sm:right-6` inset and
 * `h-11 w-11` with the others. The expansion is transient and may overlap page
 * content, so the lift and the clearance reserve the 44px column only. Like the
 * others it rides above a bottom action bar by `--floating-buttons-lift`, and it
 * tells the lift hook it is mounted so the stack is only reserved as tall as it
 * really is.
 */
export function OrderAssistantButton() {
  const { open, setOpen } = useOrderAssistant();
  useAssistantFabMounted();

  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label={ORDER_ASSISTANT_TITLE}
      aria-haspopup="dialog"
      aria-expanded={open}
      title={ORDER_ASSISTANT_TITLE}
      className={cn(
        "group fixed bottom-[calc(7.75rem+var(--floating-buttons-lift,0px))] right-2 z-50 flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-purple-600 text-white shadow-lg duration-300 hover:scale-105 hover:bg-purple-700 hover:shadow-xl motion-reduce:duration-0 sm:right-6 sm:w-auto sm:min-w-11 sm:max-w-11 sm:justify-start sm:pl-3 sm:focus-visible:max-w-52 sm:[@media(hover:hover)]:hover:max-w-52",
        FLOATING_BUTTONS_TRANSITION,
        FLOATING_BUTTONS_HIDDEN_CLASS
      )}
    >
      <Sparkles className="h-5 w-5 shrink-0" aria-hidden="true" />
      <span
        className="ml-2 hidden whitespace-nowrap pr-4 text-sm font-medium opacity-0 transition-opacity duration-300 motion-reduce:duration-0 sm:inline sm:group-focus-visible:opacity-100 sm:[@media(hover:hover)]:group-hover:opacity-100"
        aria-hidden="true"
      >
        {ORDER_ASSISTANT_TITLE}
      </span>
    </button>
  );
}
